import React from 'react';
import { Typography } from 'antd';
import { safeRenderValue } from '../utils/safeRender';

const { Text } = Typography;

/**
 * SafeText Component
 * 
 * A wrapper around Ant Design's Typography.Text that safely handles objects
 * and prevents React error #31 by ensuring only primitive values are rendered.
 */
const SafeText = ({ children, ...props }) => {
  // Process children to ensure they're safe to render
  const safeChildren = React.Children.map(children, (child) => {
    // If child is an object with {value, unit} structure, extract the value
    if (typeof child === 'object' && child !== null && !React.isValidElement(child)) {
      console.warn('SafeText: Converting object to safe string:', child);
      return safeRenderValue(child);
    }
    return child;
  });

  return <Text {...props}>{safeChildren}</Text>;
};

/**
 * SafeSpan Component
 * 
 * A safe span element that handles objects properly
 */
export const SafeSpan = ({ children, ...props }) => {
  // Process children to ensure they're safe to render
  const safeChildren = React.Children.map(children, (child) => {
    // If child is an object with {value, unit} structure, extract the value
    if (typeof child === 'object' && child !== null && !React.isValidElement(child)) {
      console.warn('SafeSpan: Converting object to safe string:', child);
      return safeRenderValue(child);
    }
    return child;
  });

  return <span {...props}>{safeChildren}</span>;
};

/**
 * SafeDiv Component
 * 
 * A safe div element that handles objects properly
 */
export const SafeDiv = ({ children, ...props }) => {
  // Process children to ensure they're safe to render
  const safeChildren = React.Children.map(children, (child) => {
    // If child is an object with {value, unit} structure, extract the value
    if (typeof child === 'object' && child !== null && !React.isValidElement(child)) {
      console.warn('SafeDiv: Converting object to safe string:', child);
      return safeRenderValue(child);
    }
    return child;
  });

  return <div {...props}>{safeChildren}</div>;
};

/**
 * SafeParagraph Component
 *
 * A safe Paragraph element that handles objects properly, especially with ellipsis
 */
export const SafeParagraph = ({ children, ...props }) => {
  const { Paragraph } = Typography;

  // Process children to ensure they're safe to render
  const safeChildren = React.Children.map(children, (child) => {
    // If child is an object with {value, unit} structure, extract the value
    if (typeof child === 'object' && child !== null && !React.isValidElement(child)) {
      console.warn('SafeParagraph: Converting object to safe string:', child);
      return safeRenderValue(child);
    }
    return child;
  });

  return <Paragraph {...props}>{safeChildren}</Paragraph>;
};

/**
 * withSafeChildren HOC
 *
 * Higher-order component that wraps any component to make its children safe
 */
export const withSafeChildren = (WrappedComponent) => {
  return React.forwardRef((props, ref) => {
    const { children, ...otherProps } = props;

    // Process children to ensure they're safe to render
    const safeChildren = React.Children.map(children, (child) => {
      // If child is an object with {value, unit} structure, extract the value
      if (typeof child === 'object' && child !== null && !React.isValidElement(child)) {
        console.warn('withSafeChildren: Converting object to safe string:', child);
        return safeRenderValue(child);
      }
      return child;
    });

    return (
      <WrappedComponent ref={ref} {...otherProps}>
        {safeChildren}
      </WrappedComponent>
    );
  });
};

export default SafeText;
